/**
 * MAZE PATROL — hand-authored 9×9 grid + 90° rotation utility.
 *
 * The maze is a 9×9 square (270×270 px at 30px/cell), centered inside
 * the 480×270 landscape canvas. The 90° rotation event happens every
 * minute; for that to work without leaving the player or thieves
 * embedded in a wall, the grid must be **rotationally symmetric**. We
 * use 4-way symmetry: rotating the layout 90° produces an identical
 * layout. That means any cell at (r,c) has the same kind as
 * (c, 8-r), (8-r, 8-c), and (8-c, r).
 *
 * Cell kinds:
 *   - WALL: corridor wall, blocks movement
 *   - BADGE: empty corridor with a security badge (collectible dot)
 *   - EMPTY: empty corridor, no badge (spawn cells + jail cell)
 *   - ALARM: alarm panel (power pellet)
 *
 * Special positions are derived from the grid, not encoded:
 *   - PIVOT_CELL = (4,4) — center, also the jail. Player spawn.
 *   - THIEF_SPAWN_CELLS = a 4-orbit (1,1) → (1,7) → (7,7) → (7,1).
 *   - Alarm panels live at the 4-orbit (3,4) → (4,5) → (5,4) → (4,3).
 *
 * Symmetry is enforced at module-load time by `assertSymmetric()`.
 * Authors edit the *quarter-domain* via `placeOrbit()` and the helper
 * mirrors to all 4 rotations; the raw grid is then verified.
 */

export const GRID_W = 9;
export const GRID_H = 9;
export const CELL_PX = 30;

export const CellKind = {
  WALL: 0,
  BADGE: 1,
  EMPTY: 2,
  ALARM: 3,
} as const;
export type CellKindValue = (typeof CellKind)[keyof typeof CellKind];

export type Layout = CellKindValue[][];

// --- Authoring -------------------------------------------------------

/** Place a cell at (r,c) AND its 90°/180°/270° rotated copies, so the
 *  resulting map is automatically 4-way rotationally symmetric. */
function placeOrbit(
  layout: Layout,
  r: number,
  c: number,
  kind: CellKindValue
): void {
  const orbit = [
    [r, c],
    [c, GRID_H - 1 - r],
    [GRID_H - 1 - r, GRID_W - 1 - c],
    [GRID_W - 1 - c, r],
  ] as const;
  for (const [rr, cc] of orbit) {
    const row = layout[rr];
    if (row) row[cc] = kind;
  }
}

/** Pivot cell — the cell that doesn't move under 90° rotation. By
 *  symmetry this is the center, (4,4). Used as the jail / player spawn. */
export const PIVOT_CELL = { r: 4, c: 4 } as const;

/** Thief spawn cells. 4-way symmetric so rotation preserves spawn
 *  validity — each rotation just relabels which thief is at which
 *  corner. Listed in (NW, NE, SE, SW) traversal order; index in
 *  CARTRIDGE_THIEF_KINDS picks which AI gets which spawn. */
export const THIEF_SPAWN_CELLS: ReadonlyArray<{ r: number; c: number }> = [
  { r: 1, c: 1 },
  { r: 1, c: 7 },
  { r: 7, c: 7 },
  { r: 7, c: 1 },
];

/** Build the base layout by:
 *   1. Filling everything with BADGE (corridor with a dot).
 *   2. Placing WALL/EMPTY/ALARM via orbit-placement so symmetry holds.
 *   3. Center cell (4,4) is the jail/spawn — EMPTY.
 *
 *   Wall design philosophy: a single ring of inner walls offset from
 *   the perimeter, with diagonal passes punched out, plus a few
 *   "pillar" walls near the center. This gives 4 corner zones with
 *   distinct shapes after each rotation while keeping every corridor
 *   reachable from every other corridor. */
function buildRawLayout(): Layout {
  const layout: Layout = [];
  for (let r = 0; r < GRID_H; r++) {
    const row: CellKindValue[] = [];
    for (let c = 0; c < GRID_W; c++) row.push(CellKind.BADGE);
    layout.push(row);
  }

  // Inner walls — 4-cell rotational orbit each.
  placeOrbit(layout, 1, 1, CellKind.WALL);
  placeOrbit(layout, 1, 2, CellKind.WALL);
  placeOrbit(layout, 2, 1, CellKind.WALL);
  placeOrbit(layout, 3, 3, CellKind.WALL);
  placeOrbit(layout, 3, 5, CellKind.WALL);

  // Alarm panels — 4-orbit on the cardinal mid-edges (one panel per
  // outer face). Symmetric under rotation, equally accessible.
  placeOrbit(layout, 0, 4, CellKind.ALARM);

  // Thief spawn cells stay BADGE-walkable; flagging them EMPTY removes
  // the spawn-on-badge "free score" awkwardness.
  for (const { r, c } of THIEF_SPAWN_CELLS) {
    const row = layout[r];
    if (row) row[c] = CellKind.EMPTY;
  }

  // Jail / pivot cell — center.
  const pivotRow = layout[PIVOT_CELL.r];
  if (pivotRow) pivotRow[PIVOT_CELL.c] = CellKind.EMPTY;

  return layout;
}

const RAW_LAYOUT: Layout = buildRawLayout();

// --- Public API ------------------------------------------------------

export function freshLayout(): Layout {
  return RAW_LAYOUT.map((row) => [...row]);
}

export function cellAt(
  layout: Layout,
  r: number,
  c: number
): CellKindValue {
  if (r < 0 || r >= GRID_H || c < 0 || c >= GRID_W) return CellKind.WALL;
  const row = layout[r];
  if (!row) return CellKind.WALL;
  return row[c] ?? CellKind.WALL;
}

export function setCell(
  layout: Layout,
  r: number,
  c: number,
  kind: CellKindValue
): void {
  const row = layout[r];
  if (!row) return;
  row[c] = kind;
}

export function isWalkable(kind: CellKindValue): boolean {
  return kind !== CellKind.WALL;
}

/**
 * Rotate a layout 90° clockwise IN PLACE. New cell (r,c) takes the
 * value of old (GRID_H - 1 - c, r). Builds a new grid then copies back
 * to avoid trampling cells mid-rotation.
 *
 * Mutates the input. Returns the same reference for convenience.
 */
export function rotateCw90(layout: Layout): Layout {
  const next: CellKindValue[][] = [];
  for (let r = 0; r < GRID_H; r++) {
    const row: CellKindValue[] = [];
    for (let c = 0; c < GRID_W; c++) {
      row.push(cellAt(layout, GRID_H - 1 - c, r));
    }
    next.push(row);
  }
  for (let r = 0; r < GRID_H; r++) {
    for (let c = 0; c < GRID_W; c++) {
      const dst = layout[r];
      const src = next[r];
      if (dst && src) dst[c] = src[c] ?? CellKind.WALL;
    }
  }
  return layout;
}

/**
 * Same 90°-cw transform applied to a (r,c) coordinate. Used to rotate
 * entity positions when the maze rotates so they end up at the same
 * conceptual spot in the new orientation.
 */
export function rotateCoordCw90(
  r: number,
  c: number
): { r: number; c: number } {
  return { r: c, c: GRID_H - 1 - r };
}

/**
 * Apply the cw90 transform to a movement direction so a thief facing
 * "right" before rotation still has a coherent facing after.
 * Directions are (dr, dc) vectors; cw90 sends (dr,dc) → (dc, -dr).
 */
export function rotateDirCw90(
  dr: number,
  dc: number
): { dr: number; dc: number } {
  return { dr: dc, dc: -dr };
}

// --- Sanity check ----------------------------------------------------

/** Confirm 4-way rotational symmetry of the constructed RAW_LAYOUT. If
 *  a future author breaks symmetry by skipping placeOrbit(), the next
 *  test run / dev boot fails immediately instead of mid-game when a
 *  rotation suddenly puts a thief inside a wall. */
function assertSymmetric(): void {
  for (let r = 0; r < GRID_H; r++) {
    for (let c = 0; c < GRID_W; c++) {
      const original = cellAt(RAW_LAYOUT, r, c);
      const rotated = cellAt(RAW_LAYOUT, GRID_H - 1 - c, r);
      if (original !== rotated) {
        throw new Error(
          `maze.ts: RAW_LAYOUT not 90°-symmetric at (${r},${c}): ${original} vs (${GRID_H - 1 - c},${r})=${rotated}`
        );
      }
    }
  }
}
assertSymmetric();

/** Count total badges in a layout — used at level-start to detect
 *  level-clear (all collected). */
export function countBadges(layout: Layout): number {
  let n = 0;
  for (let r = 0; r < GRID_H; r++) {
    for (let c = 0; c < GRID_W; c++) {
      if (cellAt(layout, r, c) === CellKind.BADGE) n += 1;
    }
  }
  return n;
}
